"use client"

import { Skeleton } from "@/app/(site)/_components/Atoms/Skeleton"
import { LoadMoreButton } from "@/app/(site)/_components/Organisms/Cards/LoadMoreCard"
import {
    type CategoryTranslations,
    formatViewingProducts
} from "@/app/(site)/_components/Organisms/ProductGrids/_utils/categoryTranslations"
import { useProductGridData } from "@/app/(site)/_components/Organisms/ProductGrids/_utils/useProductGridData"
import DesktopFilterOverlay from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Desktop/DesktopFilterOverlay"
import MobileFilterOverlay from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Mobile/MobileFilterOverlay"
import MobileStickyFilterBar from "@/app/(site)/_components/Organisms/ProductGrids/Filter/Mobile/MobileStickyFilterBar"
import { type FilterState } from "@/app/(site)/_components/Organisms/ProductGrids/filterReducer"
import ProductGridContent from "@/app/(site)/_components/Organisms/ProductGrids/ProductGridContent"
import { type GridContentBlock } from "@/app/(site)/_components/Organisms/ProductGrids/types/grid-content"
import { ViewToggle } from "@/app/(site)/_components/Organisms/ProductGrids/ViewToggle"
import { type InfinitePageData } from "@/app/(site)/_utils/_hooks/SWR/useSWRInfiniteProductListing"
import { useGridContentLayout } from "@/app/(site)/_utils/_hooks/useGridContentLayout"
import { type VariantSortPreference } from "@/app/(site)/_utils/seasonalMerchandising"
import {
    DesktopViewMode,
    MobileViewMode,
    ProductGridProvider,
    useFilterPanelContext,
    useStaticGridContext
} from "@/app/(site)/contexts/product-grid/ProductGridContext"
import { type SearchFilter } from "@/lib/depict-client/types/api/ProductListingResponseV3"
import { type FacetMappings } from "@/lib/facet-mappings"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import React, { useCallback, useRef } from "react"

type ProductGridLayoutProps = ProductGridClientProps & {
    gridRef: React.RefObject<HTMLDivElement | null>
    desktopViewMode: DesktopViewMode
    mobileViewMode: MobileViewMode
    setDesktopViewMode: (mode: DesktopViewMode) => void
    setMobileViewMode: (mode: MobileViewMode) => void
}

function ProductGridLayout({
    serverFetchedPages,
    externalId,
    params,
    nHits,
    marketCode,
    localeCode,
    depictSessionId,
    fetchLimit,
    sortPreference,
    gridContent,
    renderedGridContent,
    currentPage,
    totalPages,
    listHierarchy,
    gridRef,
    desktopViewMode,
    mobileViewMode,
    setDesktopViewMode,
    setMobileViewMode
}: ProductGridLayoutProps) {
    const { categoryTranslations } = useStaticGridContext()
    const mobileColumns = mobileViewMode as number

    return (
        <div className="relative w-full">
            <div className="relative flex">
                <DesktopFilterOverlay />
                <ProductGridBody
                    gridRef={gridRef}
                    serverFetchedPages={serverFetchedPages}
                    externalId={externalId}
                    params={params}
                    nHits={nHits}
                    marketCode={marketCode}
                    localeCode={localeCode}
                    depictSessionId={depictSessionId}
                    fetchLimit={fetchLimit}
                    sortPreference={sortPreference}
                    gridContent={gridContent}
                    renderedGridContent={renderedGridContent}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    listHierarchy={listHierarchy}
                    mobileViewMode={mobileViewMode}
                    mobileColumns={mobileColumns}
                    desktopViewMode={desktopViewMode}
                    setDesktopViewMode={setDesktopViewMode}
                    setMobileViewMode={setMobileViewMode}
                    categoryTranslations={categoryTranslations}
                />
            </div>
            <MobileStickyFilterBar />
        </div>
    )
}

type ProductGridBodyProps = {
    gridRef: React.RefObject<HTMLDivElement | null>
    serverFetchedPages: InfinitePageData[]
    externalId: string | undefined
    params: { locale: string; categories: string[] }
    nHits: number
    marketCode: string
    localeCode: string
    depictSessionId: string
    fetchLimit: number
    sortPreference?: VariantSortPreference
    gridContent: GridContentBlock[]
    renderedGridContent: Record<string, React.ReactNode>
    currentPage: number
    totalPages: number
    listHierarchy: string
    mobileViewMode: MobileViewMode
    mobileColumns: number
    desktopViewMode: DesktopViewMode
    setDesktopViewMode: (mode: DesktopViewMode) => void
    setMobileViewMode: (mode: MobileViewMode) => void
    categoryTranslations: CategoryTranslations
}

function ProductGridBody({
    gridRef,
    serverFetchedPages,
    externalId,
    params,
    nHits,
    marketCode,
    localeCode,
    depictSessionId,
    fetchLimit,
    sortPreference,
    gridContent,
    renderedGridContent,
    currentPage,
    totalPages,
    listHierarchy,
    mobileViewMode,
    mobileColumns,
    desktopViewMode,
    setDesktopViewMode,
    setMobileViewMode,
    categoryTranslations
}: ProductGridBodyProps) {
    const desktopColumns = desktopViewMode as number
    const { isFilterPanelOpen } = useFilterPanelContext()
    const effectiveDesktopColumns = desktopColumns - (isFilterPanelOpen ? 1 : 0)

    const gridRefCallback = useCallback(
        (el: HTMLDivElement | null) => {
            ;(gridRef as React.RefObject<HTMLDivElement | null>).current = el
            if (el) {
                if (!el.hasAttribute("data-desktop-cols")) {
                    el.setAttribute("data-desktop-cols", String(desktopColumns))
                }
                if (!el.hasAttribute("data-mobile-cols")) {
                    el.setAttribute("data-mobile-cols", String(mobileColumns))
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const {
        products,
        visiblePageProducts,
        activeStartPage,
        hasFiltersOrSort,
        effectiveNHits,
        displayStart,
        displayEnd,
        hasPreviousPages,
        hasMorePages,
        isLoadingMore,
        prevPageUrl,
        nextPageUrl,
        skeletonArray,
        size,
        loadMore,
        loadPrevious
    } = useProductGridData({
        serverFetchedPages,
        externalId,
        params,
        nHits,
        marketCode,
        localeCode,
        depictSessionId,
        fetchLimit,
        sortPreference,
        currentPage,
        totalPages,
        listHierarchy
    })

    const { desktopRowGroups, mobileRowGroups, desktopEagerCount } = useGridContentLayout(
        {
            visiblePageProducts,
            products,
            activeStartPage,
            hasFiltersOrSort,
            gridContent,
            desktopColumns: effectiveDesktopColumns,
            mobileColumns
        }
    )

    const viewingProductsText = formatViewingProducts(
        categoryTranslations.viewingProducts,
        displayStart,
        displayEnd,
        effectiveNHits
    )

    const noResults = Array.isArray(products) && products.length === 0

    return (
        <>
            <section className="min-w-0 flex-1">
                <ViewToggle
                    className="flex h-56 w-full items-center justify-between border-t border-col-border-neutral px-12 py-16 lg:hidden"
                    nHits={effectiveNHits}
                    modes={[MobileViewMode.Two, MobileViewMode.Three]}
                    defaultMode={mobileViewMode}
                    onModeChange={mode => setMobileViewMode(mode as MobileViewMode)}
                    disabled={noResults}
                />
                <ViewToggle
                    className="invisible hidden h-56 w-full justify-end gap-16 border-t border-col-border-neutral px-24 py-16 lg:visible lg:sticky lg:top-[56px] lg:z-[var(--z-sticky-bar)] lg:flex"
                    nHits={effectiveNHits}
                    modes={[DesktopViewMode.Four, DesktopViewMode.Six]}
                    defaultMode={desktopViewMode}
                    onModeChange={mode => setDesktopViewMode(mode as DesktopViewMode)}
                    adjustForFilterPanel
                    disabled={noResults}
                />
                {noResults ? (
                    <p className="p-12 pb-24 text-col-text-secondary Trade_Gothic_Text_16_150_0_300 lg:p-24 lg:p-32 lg:pb-0">
                        {categoryTranslations.noResults}
                    </p>
                ) : (
                    <>
                        {hasPreviousPages && (
                            <div className="flex flex-col items-center gap-16 px-12 pb-44 pt-40 lg:gap-12 lg:px-24 lg:pb-16 lg:pt-24">
                                <LoadMoreButton
                                    title={categoryTranslations.loadPreviousPage}
                                    onClick={() => loadPrevious()}
                                    href={prevPageUrl ?? ""}
                                    isLoading={isLoadingMore}
                                />
                                <p className="text-col-text-secondary Trade_Gothic_Text_12_150_0_300">
                                    {viewingProductsText}
                                </p>
                            </div>
                        )}
                        <div
                            ref={gridRefCallback}
                            role="list"
                            className="product-grid group/grid grid grid-flow-dense p-12 lg:items-start lg:p-24"
                        >
                            <ProductGridContent
                                products={products}
                                desktopRowGroups={desktopRowGroups}
                                mobileRowGroups={mobileRowGroups}
                                renderedGridContent={renderedGridContent}
                                params={params}
                                listHierarchy={listHierarchy}
                                desktopEagerCount={desktopEagerCount}
                                desktopColumns={effectiveDesktopColumns}
                            />
                            {isLoadingMore &&
                                size > serverFetchedPages.length &&
                                skeletonArray.map((_, index) => (
                                    <div key={index}>
                                        <Skeleton className="aspect-square w-full" />
                                        <Skeleton className="mt-16 h-16 w-192 rounded-sm" />
                                        <Skeleton className="mt-16 h-16 w-96 rounded-sm" />
                                    </div>
                                ))}
                        </div>
                        {effectiveNHits > 0 && (
                            <div className="flex flex-col items-center gap-12 px-12 pb-40 pt-32 lg:pb-64 lg:pt-24">
                                <p className="text-col-text-secondary Trade_Gothic_Text_12_150_0_300">
                                    {viewingProductsText}
                                </p>
                                {hasMorePages && (
                                    <LoadMoreButton
                                        title={categoryTranslations.loadMore}
                                        onClick={() => loadMore()}
                                        href={nextPageUrl ?? ""}
                                        isLoading={isLoadingMore}
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>
            <MobileFilterOverlay nHits={effectiveNHits} />
        </>
    )
}

export type ProductGridClientProps = {
    serverFetchedPages: InfinitePageData[]
    externalId: string | undefined
    initialFilterQuery: FilterState
    nHits: number
    params: { locale: string; categories: string[] }
    initialFilters: SearchFilter[]
    marketCode: string
    localeCode: string
    depictSessionId: string
    fetchLimit: number
    sortPreference?: VariantSortPreference
    gridContent: GridContentBlock[]
    renderedGridContent: Record<string, React.ReactNode>
    currentPage: number
    totalPages: number
    categoryTranslations: CategoryTranslations
    sortSplitIndex: number
    sortOpen: boolean
    initialOpenFields: string[]
    listHierarchy: string
    facetMappings: FacetMappings
}

export default function ProductGridClient(props: ProductGridClientProps) {
    const siteContext = useSiteContext()
    const gridRef = useRef<HTMLDivElement>(null)
    const desktopViewModeRef = useRef(DesktopViewMode.Four)
    const mobileViewModeRef = useRef(MobileViewMode.Two)
    const filterOpenRef = useRef(false)

    const updateDesktopCols = useCallback(() => {
        const cols = desktopViewModeRef.current - (filterOpenRef.current ? 1 : 0)
        gridRef.current?.setAttribute("data-desktop-cols", String(cols))
    }, [])

    const setDesktopViewMode = useCallback(
        (mode: DesktopViewMode) => {
            desktopViewModeRef.current = mode
            updateDesktopCols()
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    const setMobileViewMode = useCallback((mode: MobileViewMode) => {
        mobileViewModeRef.current = mode
        gridRef.current?.setAttribute("data-mobile-cols", String(mode))
    }, [])

    return (
        <ProductGridProvider
            initialFilterQuery={props.initialFilterQuery}
            siteContext={siteContext}
            listHierarchy={props.listHierarchy}
            categoryTranslations={props.categoryTranslations}
            initialFilters={props.initialFilters}
            sortSplitIndex={props.sortSplitIndex}
            sortOpen={props.sortOpen}
            initialOpenFields={props.initialOpenFields}
            filterOpenRef={filterOpenRef}
            onFilterToggle={updateDesktopCols}
            facetMappings={props.facetMappings}
        >
            <ProductGridLayout
                {...props}
                gridRef={gridRef}
                desktopViewMode={desktopViewModeRef.current}
                mobileViewMode={mobileViewModeRef.current}
                setDesktopViewMode={setDesktopViewMode}
                setMobileViewMode={setMobileViewMode}
            />
        </ProductGridProvider>
    )
}
